/**
 * Deterministic demo-only derived fields for the Campaigns UI (SCRUM-205),
 * ported from Ryan Lynch's prototype. Every value is a pure function of the
 * customer's stable id, so the same customer always renders the same stub
 * across reloads and exports.
 *
 * These are demo stubs that fill the ledger + drawer with realistic-looking
 * values until real values arrive via the ingestion ETL (SCRUM-202).
 *
 * NOTE: `portfolioId` is NOT derived here — it is the customer's real natural
 * key surfaced by the API (SCRUM-204). The drawer header + CSV use that field.
 *
 * The richer drawer stubs (contact, officer, accounts, balance trend,
 * do-not-contact) are added in Subtask C alongside the drawer that consumes
 * them; this module currently holds the foundational hash + the values the
 * ledger needs (holder age, signal decay, confidence) plus shared formatters.
 */
import type { Customer } from '../intelligence-dashboard/customer.schema';
import type { DecayClass } from './campaigns.types';

// ── Deterministic hash ────────────────────────────────────────────────────
/**
 * Cheap deterministic 32-bit string hash (Ryan's `((h<<5)-h+c)|0`, then abs).
 * Equivalent to a djb2/Java-style rolling hash. Returns a non-negative integer.
 */
export function hashString(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) - h + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Salted profile hash for per-field stubs (Ryan's `_profileHash`). */
export function profileHash(customer: Customer, salt: string): number {
  return hashString(`${customer.id}|${salt}`);
}

/**
 * Deterministically picks an element from a NON-EMPTY array by hash. Throws if
 * the array is empty (a programming error — all call sites use literal arrays).
 * Satisfies noUncheckedIndexedAccess without scattering non-null assertions.
 */
function pick<T>(arr: readonly T[], hash: number): T {
  const el = arr[hash % arr.length];
  if (el === undefined) throw new Error('pick() called on empty array');
  return el;
}

// ── Holder age ──────────────────────────────────────────────────────────────
/**
 * Demo holder age for Personal customers (18–85, deterministic from id).
 * Returns null for Business customers (no individual holder age).
 */
export function customerHolderAge(customer: Customer): number | null {
  if (customer.accountType === 'Business') return null;
  return 18 + (hashString(customer.id) % 68); // 18 .. 85
}

// ── Signal decay + confidence ─────────────────────────────────────────────
/**
 * Decay bucket for a signal's age in days (Ryan's `decayClass`):
 * ≤7d fresh (opacity 1.0), ≤30d aging (0.55), >30d stale (0.28).
 */
export function decayClass(ageDays: number): DecayClass {
  if (ageDays <= 7) return 'fresh';
  if (ageDays <= 30) return 'aging';
  return 'stale';
}

/** Opacity for a decay bucket (signal dot rendering). */
export function decayOpacity(decay: DecayClass): number {
  switch (decay) {
    case 'fresh':
      return 1.0;
    case 'aging':
      return 0.55;
    case 'stale':
      return 0.28;
  }
}

/**
 * FRESHNESS percentage for a signal of the given age (Ryan's
 * `clamp(0, 100, 100 - age * 1.5)`). Fresher signals score higher.
 *
 * SCRUM-843 — renamed from `confidenceFromAge`. This value is a pure function of
 * the signal's AGE: it says nothing about how strong the underlying evidence is,
 * so two signals triggered the same day score identically however marginal or
 * emphatic their detection was. Presenting it as "confidence" invited exactly the
 * wrong reading — a $0-balance customer appeared to carry an 81%-confident
 * Relationship Gap. The detection engine's real score is not exposed to the client
 * at all (the customers API returns only `{active, age}` per signal), so the honest
 * fix is to name and label this for what it is rather than imply a confidence the
 * client cannot see. Surfacing the true score is tracked separately.
 */
export function freshnessFromAge(ageDays: number): number {
  return Math.max(0, Math.min(100, Math.round(100 - ageDays * 1.5)));
}

// ── Formatters ────────────────────────────────────────────────────────────
/** Tenure label from months, e.g. 68 → "5y 8m", 7 → "7m", 0 → "0m". */
export function tenureLabel(ageMonths: number): string {
  const years = Math.floor(ageMonths / 12);
  const months = ageMonths % 12;
  if (years === 0) return `${String(months)}m`;
  if (months === 0) return `${String(years)}y`;
  return `${String(years)}y ${String(months)}m`;
}

/**
 * Account-opened date from a tenure in months, formatted "Mon YYYY".
 * `now` is injectable for deterministic tests.
 */
export function formatOpenedDate(ageMonths: number, now: Date = new Date()): string {
  const opened = new Date(now.getFullYear(), now.getMonth() - ageMonths, 1);
  return opened.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Relative-days label (Ryan's `formatRelativeDays`): today / yesterday / N days
 * ago / N weeks ago / N months ago / N years ago.
 */
export function formatRelativeDays(days: number): string {
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${String(days)} days ago`;
  if (days < 30) return `${String(Math.round(days / 7))} weeks ago`;
  if (days < 365) return `${String(Math.round(days / 30))} months ago`;
  return `${String(Math.round(days / 365))} years ago`;
}

/**
 * Compact money for hero stats and ICP gap annotations (SCRUM-275),
 * e.g. 4658091 → "$4.7M", 12500 → "$12.5K", 84000 → "$84K", 2000000 → "$2M".
 *
 * Drops a trailing ".0" so whole values read cleanly ("$84K" not "$84.0K") to
 * match Ryan's mockup; fractional values keep one decimal ("$12.5K", "$2.4M").
 */
export function formatCompactMoney(amount: number): string {
  const abs = Math.abs(amount);
  const trim = (n: number, suffix: string): string => {
    const fixed = n.toFixed(1);
    return `$${fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed}${suffix}`;
  };
  if (abs >= 1_000_000) return trim(amount / 1_000_000, 'M');
  if (abs >= 1_000) return trim(amount / 1_000, 'K');
  return `$${String(Math.round(amount))}`;
}

// ── Drawer demo stubs (SCRUM-207) ──────────────────────────────────────────
// Deterministic per-customer values for the detail drawer, ported from Ryan's
// prototype. Real values arrive via ingestion ETL (SCRUM-202).

const PHONE_AREA_CODES = ['540', '304', '276']; // Shenandoah Valley (Meridian footprint)

/** Demo phone, e.g. "(540) 816-7843". */
export function customerPhone(c: Customer): string {
  const h = profileHash(c, 'phone');
  const ac = pick(PHONE_AREA_CODES, h);
  const p2 = 200 + ((h >> 4) % 700);
  const p3 = 1000 + ((h >> 12) % 9000);
  return `(${ac}) ${String(p2)}-${String(p3)}`;
}

/** Demo email derived from the customer name + a deterministic domain. */
export function customerEmail(c: Customer): string {
  const nm = c.name.toLowerCase();
  const parts = nm.split(',');
  let local: string;
  if (parts.length >= 2) {
    const first = (parts[1] ?? '').trim().split(/\s+/)[0] ?? '';
    const last = (parts[0] ?? '').trim().split(/\s+/)[0] ?? '';
    local = `${first}.${last}`;
  } else {
    local = nm.replace(/[^a-z0-9]+/g, '.');
  }
  local = local.replace(/^\.+|\.+$/g, '').slice(0, 28);
  const domains =
    c.accountType === 'Business'
      ? ['gmail.com', 'outlook.com', 'company.com', 'llc.com']
      : ['gmail.com', 'outlook.com', 'yahoo.com', 'aol.com', 'comcast.net'];
  const d = pick(domains, profileHash(c, 'email'));
  return `${local}@${d}`;
}

/** Demo mailing address in the Shenandoah Valley footprint. */
export function customerAddress(c: Customer): string {
  const h = profileHash(c, 'addr');
  const streets = [
    'Main St',
    'Oak Ave',
    'Maple Dr',
    'Pine Ln',
    'Elm Rd',
    'Cedar Way',
    'Church St',
    'Mill Rd',
    'Spring St',
    'Valley View Dr',
    'Ridge Rd',
  ];
  const cities = [
    { city: 'Winchester', state: 'VA', zip: 22601 },
    { city: 'Strasburg', state: 'VA', zip: 22657 },
    { city: 'Front Royal', state: 'VA', zip: 22630 },
    { city: 'Berryville', state: 'VA', zip: 22611 },
    { city: 'Stephens City', state: 'VA', zip: 22655 },
    { city: 'Woodstock', state: 'VA', zip: 22664 },
    { city: 'Martinsburg', state: 'WV', zip: 25401 },
  ];
  const num = 100 + (h % 9800);
  const street = pick(streets, h >> 3);
  const loc = pick(cities, h >> 8);
  return `${String(num)} ${street}, ${loc.city}, ${loc.state} ${String(loc.zip)}`;
}

export interface DoNotContact {
  call: boolean;
  email: boolean;
  mail: boolean;
}

/** Demo do-not-contact flags (~6% call, ~5% email, ~3% mail). */
export function customerDoNotContact(c: Customer): DoNotContact {
  const h = profileHash(c, 'dnc');
  return {
    call: h % 100 < 6,
    email: (h >> 6) % 100 < 5,
    mail: (h >> 12) % 100 < 3,
  };
}

export interface Officer {
  name: string;
  role: string;
  code: string;
}

const OFFICERS: readonly Officer[] = [
  { name: 'Carol Whitfield', role: 'Personal Banker', code: 'CW42' },
  { name: 'Marcus Hale', role: 'Branch Manager', code: 'MH18' },
  { name: 'Priya Desai', role: 'Relationship Manager', code: 'PD07' },
  { name: 'Tom Reinholt', role: 'Commercial Banker', code: 'TR93' },
  { name: 'Janelle Cortés', role: 'Personal Banker', code: 'JC55' },
  { name: 'Daniel Kovacs', role: 'Wealth Advisor', code: 'DK21' },
  { name: 'Anika Bauer', role: 'Branch Manager', code: 'AB36' },
  { name: 'Reggie Foxworth', role: 'Commercial Banker', code: 'RF74' },
];

/** Demo assigned officer (deterministic from id). */
export function customerOfficer(c: Customer): Officer {
  return pick(OFFICERS, profileHash(c, 'officer'));
}

/**
 * Demo "days since last transaction". Insight-aware: dormant customers pull
 * deep (60–300d), otherwise recent (0–20d).
 */
export function customerLastActivity(c: Customer): number {
  const h = profileHash(c, 'lastTxn');
  if (c.insights.dormant?.active) return 60 + (h % 240);
  return h % 21;
}

export interface BalanceTrend {
  direction: 'up' | 'down' | 'flat';
  pct: number;
}

/**
 * Demo 30-day balance trend. Insight-aware: flight → down, smb → up, else a
 * small deterministic swing.
 */
export function customerBalanceTrend(c: Customer): BalanceTrend {
  const h = profileHash(c, 'trend');
  if (c.insights.flight?.active) return { direction: 'down', pct: -(3 + (h % 18)) };
  if (c.insights.smb?.active) return { direction: 'up', pct: 4 + (h % 22) };
  const v = (h % 30) - 15;
  return { direction: v > 2 ? 'up' : v < -2 ? 'down' : 'flat', pct: v };
}

const ACCOUNT_LABELS: Record<string, string> = {
  CHK: 'Checking',
  SAV: 'Savings',
  MM: 'Money Market',
  CD: 'Certificate of Deposit',
  IRA: 'IRA',
  HSA: 'HSA',
  LN: 'Loan',
  HE: 'Home Equity Loan',
  CC: 'Credit Card',
  BIZ: 'Business Account',
};

export interface DerivedAccount {
  code: string;
  label: string;
  acctNum: string;
  balance: number;
  opened: string;
}

export interface CustomerPortfolio {
  accounts: DerivedAccount[];
  concentration: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Demo per-holding account breakdown with deterministic balances (allocated by
 * weight), masked account numbers, opened dates, and a concentration label
 * ("{label}-only" / "-heavy" / "Balanced" / "Diversified" / "No products").
 */
export function customerAccounts(c: Customer, now: Date = new Date()): CustomerPortfolio {
  const holdings = c.holdings;
  if (holdings.length === 0) {
    return { accounts: [], concentration: 'No products' };
  }

  const h = profileHash(c, 'portfolio');
  const weights = holdings.map((_code, i) => 1 + ((h >> (i * 3)) % 7));
  const sumW = weights.reduce((s, w) => s + w, 0);
  const bal = c.balance;

  const accounts: DerivedAccount[] = holdings.map((code, i) => {
    const acctNum = ((profileHash(c, code + String(i)) % 9000) + 1000).toString();
    const monthsBack = c.ageMo > 0 ? profileHash(c, `${code}opened${String(i)}`) % c.ageMo : 0;
    const open = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
    const weight = weights[i] ?? 1;
    return {
      code,
      label: ACCOUNT_LABELS[code] ?? code,
      acctNum: `••${acctNum}`,
      balance: Math.round((bal * weight) / sumW),
      opened: `${MONTHS[open.getMonth()] ?? ''} ${String(open.getFullYear())}`,
    };
  });

  const byCode: Record<string, number> = {};
  for (const a of accounts) byCode[a.code] = (byCode[a.code] ?? 0) + a.balance;
  const sorted = Object.keys(byCode).sort((a, b) => (byCode[b] ?? 0) - (byCode[a] ?? 0));
  const topCode = sorted[0] ?? '';
  const topShare = bal ? (byCode[topCode] ?? 0) / bal : 0;

  let concentration: string;
  if (sorted.length === 1) concentration = `${ACCOUNT_LABELS[topCode] ?? topCode}-only`;
  else if (topShare > 0.65) concentration = `${ACCOUNT_LABELS[topCode] ?? topCode}-heavy`;
  else if (sorted.length >= 4) concentration = 'Diversified';
  else concentration = 'Balanced';

  return { accounts, concentration };
}

/** SIGNAL_REASONS — the "why did this trigger?" reason text per insight type. */
export function signalReason(c: Customer, type: 'flight' | 'smb' | 'relgap' | 'dormant'): string {
  switch (type) {
    case 'flight':
      return 'Customer balance dropped 18% over the last 30 days, and they queried CD rates twice. Flight risk threshold crossed.';
    case 'smb':
      return 'Business account showing month-over-month deposit growth above the SMB growth threshold. Strong cross-sell signal.';
    case 'relgap': {
      const n = c.holdings.length;
      const yrs = Math.floor(c.ageMo / 12);
      return `Customer holds only ${String(n)} product${n === 1 ? '' : 's'} relative to tenure of ${String(yrs)} years — under the relationship-depth benchmark.`;
    }
    case 'dormant':
      return `No transactions in the last ${String(customerLastActivity(c))} days. Dormant threshold (60d) crossed and offer window is open.`;
  }
}
